import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread, parseIntOrDefault } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';
import { resolveVariantId } from '@theme/variant-resolution';
import { ThemeEvents, PriceUpdateEvent } from '@theme/events';
// UdoPrints modules go through the import map (see scripts.liquid) — a
// relative './file.js' import resolves without the ?v= cache-busting param
// and Shopify's CDN then serves a stale copy of the module forever.
import { SKIP_APP_BLOCK } from '@theme/udoprints-editor';
import { PriceFormatter, UPSELL_PRODUCT_PREFIX } from '@theme/upsell-products';
import { detectAdditionalFreeProductsPromotion } from '@theme/free-products-promotion';
import { detectAdditionalFreeProductsPromotionV2 } from '@theme/free-products-promotion-v2';
import { detectAdditionalFreeProductsPromotionV3 } from '@theme/free-products-promotion-v3';

/**
 * UdoPrints: the editor bridge is a page-wide GLOBAL, but only the form in
 * the section that rendered the app block owns it. Any other product form on
 * the same page — "You may also like" card quick-adds, the quick-add modal —
 * must never gate on the bridge, show the export progress ring, or attach
 * this page's personalization to ITS add (a snowboard quick-added from a
 * personalized product page was landing in the cart carrying the print's
 * "Your text"/background properties — Qui, 2026-08-20).
 * @param {Element} el
 * @returns {boolean}
 */
function udoOwnsAppBlock(el) {
  if (el.closest('product-card, quick-add-component, .quick-add-modal, quick-add-dialog')) return false;
  const scope = el.closest('.shopify-section') || document;
  return !!scope.querySelector(
    'udoprints-custom-app, .udoprints-custom-app, #udoprints-extension-root, [id^="udoprints-design-preview-data"]'
  );
}

/* UdoPrints: the swatch legend's selected value ("Frame  Black") only
 * updates when the variant section render returns — on a slow connection the
 * whole picker feels unresponsive even though the ring switched instantly.
 * Write the clicked value into the legend the moment the radio changes; the
 * morph confirms it with the server's answer right after (Qui, 2026-08-21). */
document.addEventListener(
  'change',
  (e) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const fieldset = input.closest('fieldset.variant-option--swatches');
    const valueEl = fieldset?.querySelector('legend .variant-option__swatch-value');
    if (valueEl && input.value) valueEl.textContent = input.value;
  },
  true
);

/* UdoPrints: a refused personalization add DISABLES the Add To Cart button
 * (the latch), and browsers swallow clicks on disabled controls — a customer
 * clicking again got silence and no guidance. pointer-events:none on the
 * disabled button (add-to-cart-button.liquid) lets the click land on its
 * surroundings, and this delegated listener re-runs the bridge check so the
 * app re-scrolls to the first missing option on EVERY attempt. The latch
 * itself stays down (Qui, 2026-08-21). */
document.addEventListener(
  'click',
  (e) => {
    const comp = e.target instanceof Element ? e.target.closest('add-to-cart-component') : null;
    if (!comp) return;
    const btn = comp.querySelector('button[ref="addToCartButton"]');
    if (!btn || !btn.disabled) return;
    if (window.UdoPrintsEditorManager?.hasAppBlockMarkup?.() && udoOwnsAppBlock(comp)) {
      window.UdoPrintsEditorManager.isReadyToExport().catch(() => {});
    }
  },
  true
);

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * UdoPrints: the AddToCartComponent currently showing a personalization
 * export progress ring, if any. Module-scoped so ProductFormComponent (which
 * owns the fetch) can settle a ring started by whichever button was clicked.
 * @type {AddToCartComponent | null}
 */
let udoActiveAtcProgress = null;

/** UdoPrints: circumference of the 8px-radius progress ring (2πr). */
const UDO_ATC_RING_CIRCUMFERENCE = 50.27;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  /** @type {AbortController} UdoPrints: price-in-button listeners */
  #priceAbortController = new AbortController();

  /** @type {number} */
  #currentPrice = 0;

  /** @type {number} */
  #originalVariantPrice = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
    this.#listenToPriceUpdates();
    this.#initializePrice();

    // UdoPrints: register the bridge state listener EARLY, not just on the
    // first Add To Cart click — the extension broadcasts personalization
    // completeness continuously (drives the sticky bar's "Customize yours"
    // swap), and click-time registration would drop everything before the
    // first click. One delayed retry covers the manager script loading after
    // this component connects.
    const udoListen = () => {
      if (window.UdoPrintsEditorManager?.hasAppBlockMarkup?.() && udoOwnsAppBlock(this)) {
        this.listenToAppBlockState();
        return true;
      }
      return false;
    };
    if (!udoListen()) setTimeout(udoListen, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
    this.#priceAbortController.abort();
    this.cancelExportProgress();
  }

  /**
   * UdoPrints: keep the button state in sync with the personalization app
   * block while an upload is in flight (see the theme<->extension bridge).
   * `upload-progress:<n>` states carry the export's real milestones (the
   * extension emits one as each upload/import step completes) and drive the
   * progress ring shown in place of the button text.
   */
  listenToAppBlockState() {
    window.UdoPrintsEditorManager.onStateChange((params) => {
      if (params.state === 'upload-state:loading') {
        this.disable();
      } else if (params.state === 'upload-state:success') {
        this.enable();
      } else if (typeof params.state === 'string' && params.state.startsWith('upload-progress:')) {
        const percent = Number(params.state.slice('upload-progress:'.length));
        // Only feed an ALREADY-ACTIVE ring. Milestones must never spawn one:
        // a late/straggler milestone after the add settled would re-create a
        // ring AFTER the checkmark burst — the "v" only comes after 100%.
        if (Number.isFinite(percent)) udoActiveAtcProgress?.setExportProgress(percent);
      } else if (params.state === 'personalization:complete' || params.state === 'personalization:incomplete') {
        // Re-broadcast for the sticky bar — the bridge has a single
        // state-change slot (same pattern as udo:atc-progress). The window
        // flag lets late subscribers read the latest state on connect.
        const complete = params.state === 'personalization:complete';
        window.__udoPersonalizationComplete = complete;
        document.dispatchEvent(new CustomEvent('udo:personalization-state', { detail: { complete } }));
      }
    });
  }

  /* UdoPrints: personalization export progress ring.
     Milestones are REAL — each one marks a completed network step of the
     export (original import, preview upload + CDN wait, supplier upload,
     generated import) and finally the /cart/add response. Between milestones
     the ring creeps slowly toward a ceiling just above the last real value so
     it never looks frozen, and never claims completion early. */

  /** @type {number} */
  #exportPct = 0;

  /** @type {number} */
  #exportCeiling = 0;

  /** @type {number | null} */
  #exportTimer = null;

  /** @type {number | null} */
  #exportSafetyTimer = null;

  /** @type {HTMLElement | null} */
  #exportOverlay = null;

  /** @type {HTMLElement | null} */
  #exportNote = null;

  get exportProgressActive() {
    return this.#exportOverlay !== null;
  }

  startExportProgress() {
    if (this.#exportOverlay) return;
    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;
    udoActiveAtcProgress = this;
    // A leftover added-state from a recent add keeps the checkmark visible
    // (and the button text transparent) — the customer would see the "v"
    // BEFORE the percentage. The ring owns the button now; the checkmark
    // returns only via finishExportProgress after 100%.
    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
      this.#resetTimeouts = [];
    }
    addToCartButton.removeAttribute('data-added');
    const overlay = document.createElement('span');
    overlay.className = 'udo-atc-progress';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML =
      `<span class="udo-atc-progress__label">0%</span>` +
      `<svg class="udo-atc-progress__ring" viewBox="0 0 20 20" width="18" height="18">` +
      `<circle class="udo-atc-progress__track" cx="10" cy="10" r="8"></circle>` +
      `<circle class="udo-atc-progress__fill" cx="10" cy="10" r="8"></circle>` +
      `</svg>`;
    addToCartButton.appendChild(overlay);
    addToCartButton.classList.add('udo-atc-progress-active');
    this.#exportOverlay = overlay;

    // Reassurance line under the button while the export runs. Shares the
    // overlay's fade-in delay, so a ring cancelled within milliseconds
    // (normal product on the default template) never shows it.
    const note = document.createElement('div');
    note.className = 'udo-atc-wait-note';
    note.setAttribute('aria-live', 'polite');
    note.innerHTML =
      `<span class="udo-atc-wait-note__text">Hang tight — preparing your personalized item</span>` +
      `<span class="udo-atc-wait-note__dots" aria-hidden="true"><span></span><span></span><span></span></span>`;
    this.insertAdjacentElement('afterend', note);
    this.#exportNote = note;

    this.#exportPct = 0;
    this.#exportCeiling = 14;
    this.#renderExportProgress(3);
    this.#exportTimer = setInterval(() => {
      if (this.#exportPct < this.#exportCeiling) this.#renderExportProgress(this.#exportPct + 0.5);
    }, 200);
    // Never leave a stuck ring — matches the editor manager's 60s ceiling.
    this.#exportSafetyTimer = setTimeout(() => this.cancelExportProgress(), 65000);
  }

  /** @param {number} percent - A completed real milestone (0-100). */
  setExportProgress(percent) {
    if (!this.#exportOverlay) this.startExportProgress();
    this.#exportCeiling = Math.min(percent + 12, 95);
    this.#renderExportProgress(Math.max(this.#exportPct, percent));
  }

  /**
   * Ring reaches 100% with a small pop, holds a beat so completion registers,
   * then hands off to the theme's default added animation (checkmark burst +
   * fly-to-cart).
   */
  finishExportProgress() {
    if (!this.#exportOverlay) return;
    this.#stopExportTimers();
    this.#renderExportProgress(100);
    this.#exportOverlay.classList.add('udo-atc-progress--done');
    setTimeout(() => {
      this.#removeExportOverlay();
      if (this.dataset.addToCartAnimation === 'true') this.#animateFlyToCart();
      this.animateAddToCart();
      document.dispatchEvent(new CustomEvent('udo:atc-progress-finish'));
    }, 400);
  }

  /** Removes the ring with no success animation (gating failure / error). */
  cancelExportProgress() {
    if (!this.#exportOverlay) return;
    this.#stopExportTimers();
    this.#removeExportOverlay();
    document.dispatchEvent(new CustomEvent('udo:atc-progress-cancel'));
  }

  /**
   * The gate resolved "not a personalization add" (the app block wrapper
   * renders on every product, so the click optimistically started a ring) —
   * drop the ring and play the stock instant feedback instead. The gate
   * settles within milliseconds, before the ring's fade-in delay makes it
   * visible, so the customer only ever sees the stock burst.
   */
  playStockAddFeedback() {
    this.cancelExportProgress();
    if (this.dataset.addToCartAnimation === 'true') this.#animateFlyToCart();
    this.animateAddToCart();
  }

  /** @param {number} percent */
  #renderExportProgress(percent) {
    this.#exportPct = Math.min(percent, 100);
    const overlay = this.#exportOverlay;
    if (!overlay) return;
    const fill = /** @type {SVGCircleElement | null} */ (overlay.querySelector('.udo-atc-progress__fill'));
    if (fill) fill.style.strokeDashoffset = String(UDO_ATC_RING_CIRCUMFERENCE * (1 - this.#exportPct / 100));
    const label = overlay.querySelector('.udo-atc-progress__label');
    if (label) label.textContent = `${Math.round(this.#exportPct)}%`;
    // Broadcast so other surfaces (the sticky bar) can mirror the ring on
    // their own button — there is only one manager state-change slot, so
    // they cannot subscribe to the bridge themselves.
    document.dispatchEvent(new CustomEvent('udo:atc-progress', { detail: { percent: this.#exportPct } }));
  }

  #stopExportTimers() {
    if (this.#exportTimer !== null) {
      clearInterval(this.#exportTimer);
      this.#exportTimer = null;
    }
    if (this.#exportSafetyTimer !== null) {
      clearTimeout(this.#exportSafetyTimer);
      this.#exportSafetyTimer = null;
    }
  }

  #removeExportOverlay() {
    this.#exportOverlay?.remove();
    this.#exportOverlay = null;
    this.#exportNote?.remove();
    this.#exportNote = null;
    this.refs.addToCartButton?.classList.remove('udo-atc-progress-active');
    if (udoActiveAtcProgress === this) udoActiveAtcProgress = null;
  }

  /**
   * UdoPrints price-in-button: listen for price delta events (upsells /
   * personalization options) and update the price shown in the button.
   */
  #listenToPriceUpdates() {
    const { signal } = this.#priceAbortController;
    document.addEventListener(ThemeEvents.priceUpdate, this.#onPriceUpdate, { signal });
  }

  /**
   * @param {PriceUpdateEvent} event
   */
  #onPriceUpdate = (event) => {
    const { delta, sourceId } = event.detail;
    if (sourceId && sourceId !== this.id && !this.closest(`[data-section-id="${sourceId}"]`)) {
      return;
    }
    this.#currentPrice += delta;
    this.#updatePriceDisplay();
  };

  #updatePriceDisplay() {
    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;
    const priceElement = addToCartButton.querySelector('.add-to-cart-text__price');
    if (!priceElement) return;
    priceElement.textContent = PriceFormatter.formatMoney(this.#currentPrice);
  }

  #initializePrice() {
    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;
    const priceElement = addToCartButton.querySelector('.add-to-cart-text__price');
    if (!priceElement) return;
    const originalPriceString = priceElement.getAttribute('data-price') ?? this.#getFallbackPrice() ?? '0';
    const originalPrice = parseFloat(originalPriceString);
    if (this.#originalVariantPrice !== originalPrice) {
      this.#currentPrice = this.#currentPrice - this.#originalVariantPrice + originalPrice;
      this.#originalVariantPrice = originalPrice;
    }
    this.#updatePriceDisplay();
  }

  #getFallbackPrice() {
    const { addToCartButton } = this.refs;
    const priceElement = addToCartButton?.querySelector('.add-to-cart-text__price');
    if (!priceElement) return null;
    const priceMatch = (priceElement.textContent?.trim() || '').match(/[\d,]+\.?\d*/);
    return priceMatch ? priceMatch[0].replace(/,/g, '') : null;
  }

  /**
   * Resets the shown price to the current variant's base price (call after a
   * variant-change morph replaces the button contents).
   */
  resetPrice() {
    this.#initializePrice();
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    // UdoPrints: a personalization export is already in flight — swallow the
    // repeat tap entirely (preventDefault stops the form submission). A
    // second concurrent export finishes out of order and can replay the
    // success burst while the visible ring is still counting.
    if (udoActiveAtcProgress) {
      event.preventDefault();
      return;
    }


    const form = this.closest('form');
    if (!form?.checkValidity()) return;
    this.listenToAppBlockState();

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      // UdoPrints: on personalization products the export takes seconds, so
      // an instant success burst here would be a lie. Show the progress ring
      // instead; finishExportProgress plays the burst when the add really
      // lands, and a gating failure cancels the ring with no animation.
      if (window.UdoPrintsEditorManager?.hasAppBlockMarkup?.() && udoOwnsAppBlock(this)) {
        this.startExportProgress();
      } else {
        if (animationEnabled && !event.target.closest('.quick-add-modal')) {
          this.#animateFlyToCart();
        }
        this.animateAddToCart();
      }
    } else if (window.UdoPrintsEditorManager?.hasAppBlockMarkup?.() && udoOwnsAppBlock(this)) {
      // Sticky-bar (puppet) tap on a personalization product: run the ring
      // machinery here as usual — sticky-add-to-cart.js mirrors it onto the
      // sticky button via the udo:atc-progress events.
      this.startExportProgress();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
    // UdoPrints: also warm the cart-row thumbnail sizes (the drawer renders
    // item.image at width: 250, whose srcset offers 165w/250w candidates).
    // Cart rows are loading="lazy", so without this the image only starts
    // downloading AFTER the drawer morph — visible pop-in. Warming here
    // (pointerenter, before the click) means the row paints complete.
    preloadImage(image.replace(/([?&])width=\d+/, '$1width=250'));
    preloadImage(image.replace(/([?&])width=\d+/, '$1width=165'));
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @typedef {object} QueuedAddToCartItem
 * @property {number} quantity - The quantity captured when Add was clicked.
 * @property {number} generation - The variant-change generation active when Add was clicked.
 * @property {string | null} intendedVariantId - The selected option's data-variant-id, when present.
 * @property {Promise<unknown> | null} pendingVariantChange - The server-side section fetch for the clicked selection.
 * @property {string | null} variantResolutionUrl - A section-rendering URL that resolves the clicked selection.
 *
 * @typedef {object} QuantityConstraints
 * @property {string} min
 * @property {string | null} max
 * @property {string} step
 * @property {string | null} cartQuantity
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /**
   * Adds queued while a variant change is in flight. Each entry captures the selection state and
   * generation active when Add was clicked, then resolves that selection at drain time.
   * @type {QueuedAddToCartItem[]}
   */
  #addToCartQueue = [];

  /**
   * The in-flight variant-change section fetch promise. The queue drain awaits this before
   * reading the resolved variant id.
   * @type {Promise<unknown> | null}
   */
  #pendingVariantChange = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    // Fallback for pages without cart-items-component (e.g. page-based cart on product pages)
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const cartItem = cart.items.find(
      /** @param {any} item */
      (item) => item.variant_id.toString() === variantIdInput.toString()
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;

        if (detail?.items) {
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  async handleSubmit(event) {
    event.preventDefault();

    // UdoPrints: gate BEFORE queueing so a blocked personalization never
    // plays a false success animation.
    if (this.#variantChangeInProgress) {
      const { success, feature } = await this.#checkAppBlockState();
      if (!success && !!feature) {
        udoActiveAtcProgress?.cancelExportProgress();
        return;
      }
      if (!feature) udoActiveAtcProgress?.cancelExportProgress();
      if (this.#variantChangeInProgress) {
        this.#addToCartQueue.push(this.#createQueuedAddToCartItem());
        if (!this.refs.addToCartButtonContainer?.exportProgressActive) {
          this.refs.addToCartButtonContainer?.animateAddToCart?.();
        }
        return;
      }
      // The variant change settled while gating ran — process normally.
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /** @returns {QueuedAddToCartItem} */
  #createQueuedAddToCartItem() {
    const picker = this.#getVariantPicker();
    const selectedOption = picker?.selectedOption;

    return {
      quantity: this.#getQuantity(),
      generation: this.#variantChangeGeneration,
      intendedVariantId: selectedOption?.dataset.variantId ?? null,
      pendingVariantChange: this.#pendingVariantChange,
      variantResolutionUrl: picker && selectedOption ? picker.buildRequestUrl(selectedOption) : null,
    };
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  async #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    // UdoPrints: gate on the personalization app block before anything is
    // sent — a not-ready block either opens the editor flow or blocks the add.
    const { success, feature } = await this.#checkAppBlockState();
    if (!success && !!feature) {
      udoActiveAtcProgress?.cancelExportProgress();
      return;
    }
    if (!feature) udoActiveAtcProgress?.playStockAddFeedback();

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    const sectionsToRender = cartItemComponentsSectionIds.join(',');

    // UdoPrints: build a JSON items payload so the add can carry the
    // personalization line-item properties plus any upsell / free-promotion
    // companion items. `sections` rides at the TOP level of the body — nested
    // inside an item it is silently ignored and the response loses the
    // rendered drawer HTML.
    const formDataAsJson = Object.fromEntries(formData.entries());
    delete formDataAsJson['sections'];

    const udoProductId = formDataAsJson['product-id'];
    const udoVariantId = formDataAsJson['id'];
    if (!udoProductId || !udoVariantId) throw new Error('Product ID or variant ID is missing from form data');

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    const cartLinesUpdateEvent = new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [
        {
          merchandiseId: /** @type {string} */ (formData.get('id')),
          quantity: itemCount,
        },
      ],
      promise: deferredEventPromise.promise,
    });
    // UdoPrints: personalized adds keep the drawer CLOSED until the item is
    // truly in the cart (same choreography as the empty-cart flow) — the
    // progress ring + wait note on the button are the feedback during the
    // export, and cart-drawer.js opens on the promise resolution with the
    // item already rendered. Non-personalized adds keep the instant open.
    if (feature) /** @type {any} */ (cartLinesUpdateEvent).udoDeferDrawerOpen = true;
    this.dispatchEvent(cartLinesUpdateEvent);

    // UdoPrints: the personalization export (image upload + composite) can
    // take seconds — it runs AFTER the CartLinesUpdateEvent dispatch above so
    // the drawer reacts immediately; an export failure flows into the same
    // .catch as a failed add (deferred promise rejected + CartErrorEvent).
    (async () => {
      const lineItemProperties = await this.#getLineItemProperties(
        feature,
        udoProductId.toString(),
        udoVariantId.toString()
      );
      Object.assign(formDataAsJson, { properties: lineItemProperties });

      // UdoPrints: the cart row shows the design preview as its thumbnail
      // (loading="lazy", raw CDN URL). Warm it now, while /cart/add runs,
      // so the drawer opens with the personalized preview already painted.
      const previewImageUrl = lineItemProperties['View your design'];
      if (typeof previewImageUrl === 'string' && previewImageUrl) preloadImage(previewImageUrl);

      const { cleanFormJsonData, upsellProducts } = this.#detectUpsellProducts(formDataAsJson);

      const productHandle = formDataAsJson['product-handle']?.toString() || '';
      const collectionHandles = formDataAsJson['collection-handles']?.toString().split(',') || [];
      const additionalProducts = [
        ...upsellProducts,
        ...detectAdditionalFreeProductsPromotion([productHandle, ...collectionHandles]),
        ...detectAdditionalFreeProductsPromotionV2([productHandle, ...collectionHandles]),
        ...detectAdditionalFreeProductsPromotionV3([productHandle, ...collectionHandles]),
      ];

      return fetch(Theme.routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [cleanFormJsonData, ...additionalProducts],
          sections: sectionsToRender,
          sections_url: window.location.pathname,
        }),
      });
    })()
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          udoActiveAtcProgress?.cancelExportProgress();
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        } else {
          // UdoPrints: the add truly landed — the ring reaches 100% and the
          // real success burst plays (no-op when no ring is active).
          udoActiveAtcProgress?.finishExportProgress();

          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // UdoPrints app-block bookkeeping (cart token + line-item key) is
          // handled by the background reconciliation fetch below — one cart
          // fetch serves both, instead of a second parallel /cart.json.

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // UdoPrints: resolve NOW, straight from the /cart/add response — it
          // already carries the rendered drawer sections, so the drawer can
          // paint in the same round trip (the old theme's contract). Stock
          // 4.1.3 awaited a second /cart.js fetch here, which added a full
          // serial round trip before any cart content appeared. The badge
          // total is computed instantly from the current bubble + what was
          // just added; the background reconciliation below silently corrects
          // it if the math drifted (e.g. a stock-capped add).
          const cartIconEl = /** @type {any} */ (document.querySelector('cart-icon'));
          const instantTotal = (Number(cartIconEl?.currentCartCount) || 0) + itemCount;

          deferredEventPromise.resolve({
            cart: undefined,
            detail: {
              items: response.items,
              source: 'product-form-component',
              sourceId: this.id.toString(),
              itemCount: instantTotal,
              productId: this.dataset.productId,
              sections: response.sections,
              didError: false,
            },
          });

          // Background reconciliation: true totals + per-variant quantity
          // sync + UdoPrints app-block bookkeeping (cart token + added item
          // key), all off one cart fetch, without holding up the drawer.
          this.#refreshCart()
            .then((ajaxCart) => {
              if (this.#getVariantIdInput()) {
                this.#updateCartQuantity(ajaxCart);
              }
              const trueCount = Number(ajaxCart?.item_count);
              if (Number.isFinite(trueCount) && trueCount !== instantTotal) {
                cartIconEl?.renderCartBubble?.(trueCount, false);
              }
              this.#successAddToCart(ajaxCart?.['token'], response?.['items']?.[0]?.key);
            })
            .catch((error) => console.warn('[product-form] cart refresh failed:', error));
        }
      })
      .catch((error) => {
        udoActiveAtcProgress?.cancelExportProgress();
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          udoActiveAtcProgress?.cancelExportProgress();
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount: totalQuantity,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(response.message));
          }
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        }

        // UdoPrints: settle a progress ring left by a queued personalized add.
        udoActiveAtcProgress?.finishExportProgress();

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'product-form-component',
            sourceId: this.id.toString(),
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
            didError: false,
          },
        });
        this.#updateCartQuantity(cart);
      })
      .catch((error) => {
        udoActiveAtcProgress?.cancelExportProgress();
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    // Track generation to prevent a stale (aborted) call from clearing the flag
    // while a newer variant selection is still pending.
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;
    // Hold the in-flight section-fetch promise so the queue drain can await it before reading the
    // resolved variant id.
    this.#pendingVariantChange = event.promise;

    try {
      const { detail } = await event.promise;
      if (!detail?.html) return;

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';

      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
        this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      // Update the button state
      if (resource == null || resource.available == false) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
        // UdoPrints: re-init the price-in-button from the morphed data-price.
        currentAddToCartButtonContainer.resetPrice();
      }

      if (acceleratedCheckoutButtonContainer) {
        if (resource == null || resource.available == false) {
          acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      }

      // Set the data attribute for the product variant media if it exists
      if (resource) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia + '&width=100'
          );
        }
      }

      // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      // Update quantity selector's min/max/step attributes and cart quantity for the new variant
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        // Keep data-quantity-default attribute in sync with new variant's minimum quantity
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        // Store quantity value before morphing entire container
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          // Get the NEW quantity selector after morphing and update its constraints
          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            // Temporarily set the old value so updateConstraints can snap it properly
            this.refs.quantitySelector.setValue(currentQuantityValue);
            // updateConstraints will snap to valid increment if needed
            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step
            );
            // Keep data-quantity-default attribute in sync with new variant's minimum quantity
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }

      // Morph volume pricing if it exists
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) return;

      // Fetch and update cart quantity for the new variant
      this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
    } finally {
      // Only clear the flag if no newer variant selection has started
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;

        // Drain any queued add-to-cart requests that accumulated during the variant change
        await this.#drainAddToCartQueue();
      }
    }
  };

  /**
   * Drains the add-to-cart queue accumulated while a variant change was in flight.
   *
   * Each queued add resolves against the selection and generation that were active when Add was
   * clicked. If no variant resolves, that queued add is aborted so a stale, empty, or maxed
   * variant id is never sent. The add-to-cart button is already disabled for unavailable
   * selections in #onProductSelect, so no further UI change is needed.
   */
  async #drainAddToCartQueue() {
    if (this.#addToCartQueue.length === 0) return;

    const queuedItems = [...this.#addToCartQueue];
    this.#addToCartQueue = [];

    /** @type {Array<{variantId: string, quantity: number}>} */
    const resolvedItems = [];
    for (const item of queuedItems) {
      const resolvedItem = await this.#resolveQueuedAddToCartItem(item);
      if (resolvedItem) {
        resolvedItems.push(resolvedItem);
      }
    }

    // UdoPrints: drain through the full single-add path instead of
    // #processBatchAddToCart so queued adds get the same gating +
    // personalization line-item export as direct clicks.
    for (const resolvedItem of resolvedItems) {
      await this.#processAddToCart(resolvedItem.variantId, resolvedItem.quantity);
    }
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string, quantity: number} | null>}
   */
  async #resolveQueuedAddToCartItem(item) {
    const { variantId, quantityConstraints } = await this.#resolveQueuedVariant(item);
    if (!variantId) return null;

    return {
      variantId,
      quantity: this.#normalizeQueuedQuantity(item.quantity, quantityConstraints),
    };
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string | null, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveQueuedVariant(item) {
    /** @type {string | null} */
    let resolvedVariantId = null;
    /** @type {boolean | undefined} */
    let available;
    /** @type {QuantityConstraints | null} */
    let quantityConstraints = null;

    if (item.pendingVariantChange) {
      try {
        const result = /** @type {{detail?: {resource?: any, html?: Document | Element}}} */ (
          await item.pendingVariantChange
        );
        const resource = result?.detail?.resource;
        quantityConstraints = this.#getQuantityConstraintsFromHtml(result?.detail?.html);
        if (resource === null) {
          available = false;
        } else if (resource) {
          resolvedVariantId = resource.id != null ? String(resource.id) : null;
          available = resource.available !== false;
        }
      } catch {
        const resolvedVariant = await this.#resolveVariantFromUrl(item.variantResolutionUrl).catch(() => ({
          variantId: null,
          available: false,
          quantityConstraints: null,
        }));
        resolvedVariantId = resolvedVariant.variantId;
        available = resolvedVariant.available;
        quantityConstraints = resolvedVariant.quantityConstraints;
      }
    }

    const isLatestGeneration = item.generation === this.#variantChangeGeneration;
    const variantId = resolveVariantId({
      resolvedVariantId,
      intendedVariantId: item.intendedVariantId,
      hiddenInputValue: isLatestGeneration ? this.#getVariantIdInput() ?? null : null,
      available: available ?? (isLatestGeneration ? !this.#isAddToCartDisabled() : undefined),
    });

    return { variantId, quantityConstraints };
  }

  /**
   * @param {number} quantity
   * @param {QuantityConstraints | null} quantityConstraints
   * @returns {number}
   */
  #normalizeQueuedQuantity(quantity, quantityConstraints) {
    if (!quantityConstraints) return quantity;

    const min = parseIntOrDefault(quantityConstraints.min, 1);
    const max = parseIntOrDefault(quantityConstraints.max, null);
    const step = parseIntOrDefault(quantityConstraints.step, 1);
    const cartQuantity = parseIntOrDefault(quantityConstraints.cartQuantity, 0);
    const effectiveMax = max === null ? null : Math.max(max - cartQuantity, min);

    let normalizedQuantity = quantity;
    if ((quantity - min) % step !== 0) {
      normalizedQuantity = min + Math.floor((quantity - min) / step) * step;
    }

    return Math.max(min, Math.min(effectiveMax ?? Infinity, normalizedQuantity));
  }

  /**
   * @param {Document | Element | null | undefined} html
   * @returns {QuantityConstraints | null}
   */
  #getQuantityConstraintsFromHtml(html) {
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      html?.querySelector?.('quantity-selector-component input[ref="quantityInput"]') ?? null
    );
    if (!quantityInput) return null;

    return {
      min: quantityInput.min,
      max: quantityInput.max || null,
      step: quantityInput.step,
      cartQuantity: quantityInput.getAttribute('data-cart-quantity'),
    };
  }

  /**
   * Resolves a queued selection using the server-side section renderer when the original in-flight
   * request was aborted by a later variant selection.
   * @param {string | null} variantResolutionUrl
   * @returns {Promise<{variantId: string | null, available: boolean | undefined, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveVariantFromUrl(variantResolutionUrl) {
    if (!variantResolutionUrl) return { variantId: null, available: undefined, quantityConstraints: null };

    const response = await fetch(variantResolutionUrl, { credentials: 'same-origin' });
    if (!response.ok) return { variantId: null, available: false, quantityConstraints: null };

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const quantityConstraints = this.#getQuantityConstraintsFromHtml(html);
    const textContent = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
    if (!textContent) return { variantId: null, available: false, quantityConstraints };

    const resource = JSON.parse(textContent);
    if (!resource || typeof resource !== 'object') return { variantId: null, available: false, quantityConstraints };

    return {
      variantId: resource.id != null ? String(resource.id) : null,
      available: resource.available !== false,
      quantityConstraints,
    };
  }

  /**
   * @returns {import('@theme/variant-picker').default | null}
   */
  #getVariantPicker() {
    const container = this.closest('product-card') ?? this.closest('dialog') ?? this.closest('.shopify-section');
    const pickers = /** @type {import('@theme/variant-picker').default[]} */ (
      Array.from(container?.querySelectorAll('variant-picker, swatches-variant-picker-component') ?? [])
    );
    const productId = this.dataset.productId;

    if (productId) {
      const matchingPicker = pickers.find((picker) => picker.dataset.productId === productId);
      if (matchingPicker) return matchingPicker;
    }

    return pickers.length === 1 ? pickers[0] ?? null : null;
  }

  /**
   * Whether the current selection's add-to-cart button is disabled (unavailable selection).
   * @returns {boolean}
   */
  #isAddToCartDisabled() {
    const containers = /** @type {NodeListOf<AddToCartComponent>} */ (this.querySelectorAll('add-to-cart-component'));
    return Array.from(containers).some((container) => container.refs.addToCartButton?.disabled);
  }

  /* UdoPrints Editor Integration */
  /* ========================== */
  /**
   * Check if the UdoPrints Editor App Block is ready to export.
   * If it is not, trigger the image uploader.
   * @returns {Promise<{success: boolean, feature: string | null}>} The App Block state.
   */
  async #checkAppBlockState() {
    // A form that doesn't own the app block never consults the bridge —
    // same contract as SKIP_APP_BLOCK: no gate, stock add, no properties.
    if (!udoOwnsAppBlock(this)) return { success: false, feature: null };
    try {
      const { success, feature, error } = await window.UdoPrintsEditorManager.isReadyToExport();
      if (!success && !!feature) {
        if (error) {
          // If the error is SKIP_APP_BLOCK, just return
          if (error === SKIP_APP_BLOCK) return { success: false, feature: null };
          // If there is another error, disable the `add to cart` button
          this.refs.addToCartButtonContainer?.disable();
          console.warn('UdoPrints Editor App Block is not ready:', error);
        }
        // If there is no error, trigger the image uploader
        else await window.UdoPrintsEditorManager.uploadImage();
      } else {
        // Enable the add to cart button
        this.refs.addToCartButtonContainer?.enable();
      }
      return { success, feature };
    } catch (error) {
      console.error('Error checking App Block state:', error);
      return { success: false, feature: null };
    }
  }

  /**
   * Get the cart data.
   * @returns {Promise<Record<string, any> | null>} The cart data.
   */
  async #getCartData() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`);
      return await response.json();
    } catch (error) {
      console.error('Error getting cart data:', error);
      return null;
    }
  }

  /**
   * Get the line item properties for the App Block.
   * @param {string | null} feature - The feature of the App Block.
   * @param {string} productId - The product ID.
   * @param {string} variantId - The variant ID.
   * @returns {Promise<Record<string, any>>} The line item properties.
   */
  async #getLineItemProperties(feature, productId, variantId) {
    if (!feature) return {};
    /** @type {Record<string, any>} */
    let properties = {};
    const { originalImage, previewImage, supplierImage, generatedImage, personalization } =
      await window.UdoPrintsEditorManager.onAddToCart({ productId, variantId });
    // The app only hands over a generated image when Studio's AI Effect is
    // configured for the product. Without one, the artwork is finished by hand
    // — tag the line item so fulfillment can route it to the artist.
    if (!generatedImage) properties._designed_by = 'Our artist';
    // Personalization the customer chose. Keys without a leading underscore are
    // shown in the cart so they can check their own input before paying.
    if (personalization) {
      for (const option of personalization.options ?? []) {
        if (option?.label && option?.value) properties[option.label] = option.value;
      }
      if (personalization.customText) properties['Your text'] = personalization.customText;
      if (personalization.predefinedImage?.label) properties['Design'] = personalization.predefinedImage.label;
      if (personalization.backgroundUrl) properties._background_image = personalization.backgroundUrl;
    }
    // No composited artwork. Two ways to get here:
    //  - Artwork Preview off: no canvas runs, so there's no preview or supplier
    //    image — only the customer's uploaded photo, which a designer finishes
    //    by hand and emails back. Record just the original.
    //  - Photo option optional and skipped: no images at all — keep the
    //    personalization collected above rather than throwing it away.
    // The final `||` throw still fires for a genuinely partial export (e.g. a
    // preview with no supplier), which is a real bug, not a valid order.
    if (!previewImage && !supplierImage) {
      if (originalImage) {
        properties = {
          _original_image: originalImage.cdnUrl,
          _tmp_original_image: originalImage.url,
          ...properties,
        };
        // Customer-visible link to their own upload — shows in the cart
        // drawer (as an in-page popup link) and on checkout (Qui, 2026-08-20).
        properties['Your Image'] = originalImage.cdnUrl;
      }
      return properties;
    }
    if (!previewImage || !originalImage || !supplierImage) throw new Error('Failed to get line item properties');
    properties = {
      // Shopify's checkout renders a URL-valued property as a link whose text
      // IS the property key, so this key is written as customer-facing copy.
      // Renaming it means updating cart-products.liquid in lockstep.
      'View your design': previewImage.cdnUrl,
      _supplier_image: supplierImage.cdnUrl,
      _original_image: originalImage.cdnUrl,
      _tmp_preview_image: previewImage.url,
      _tmp_supplier_image: supplierImage.url,
      _tmp_original_image: originalImage.url,
      ...properties,
    };
    // The app only hands over a generated image when Studio's AI Effect is
    // configured for the product, so its presence is the whole condition —
    // no product-feature branch to keep in sync with Studio.
    if (generatedImage) {
      properties = {
        _generated_image: generatedImage.cdnUrl,
        _tmp_generated_image: generatedImage.url,
        ...properties,
      };
    }
    // Customer-visible link to their own upload, alongside the preview
    // (Qui, 2026-08-20).
    properties['Your Image'] = originalImage.cdnUrl;
    return properties;
  }

  /**
   * Successfully add to cart.
   * @param {string} cartToken - The cart token.
   * @param {string} itemKey - The added item key.
   */
  async #successAddToCart(cartToken, itemKey) {
    try {
      await window.UdoPrintsEditorManager.afterAddToCart({ itemKey, cartToken });
    } catch (error) {
      console.error('App Block error after adding to cart:', error);
    }
  }

  /**
   * Detect upsell products in the form data.
   * @param {Record<string, any>} formJsonData - The form data containing product information.
   * @returns {{cleanFormJsonData: Record<string, any>, upsellProducts: import('./free-products-promotion.js').AdditionalProduct[]}} The cleaned form data and the upsell products.
   */
  #detectUpsellProducts(formJsonData) {
    const upsellProductsFormData = Object.entries(formJsonData).filter(([key]) => key.startsWith(UPSELL_PRODUCT_PREFIX));
    if (upsellProductsFormData.length === 0) return { cleanFormJsonData: formJsonData, upsellProducts: [] };
    const productTitle = formJsonData['product-title'];
    if (!productTitle) {
      console.error('Product title is missing from form data');
      window.UdoPrintsEditorManager.onError('Product title is missing from form data');
      return { cleanFormJsonData: formJsonData, upsellProducts: [] };
    }
    /** @type {import('./free-products-promotion.js').AdditionalProduct[]} */ const upsellProducts = [];
    Object.entries(formJsonData).forEach(([key, value]) => {
      if (key.startsWith(UPSELL_PRODUCT_PREFIX)) {
        upsellProducts.push({
          id: key.replace(UPSELL_PRODUCT_PREFIX, ''),
          title: value,
          quantity: 1,
          properties: {
            Product: productTitle,
          },
        });
      }
    });
    // Remove the upsell products from the form data
    const cleanFormJsonData = Object.fromEntries(
      Object.entries(formJsonData).filter(([key]) => !key.startsWith(UPSELL_PRODUCT_PREFIX))
    );
    const formDataProperties = {
      ...cleanFormJsonData['properties'],
      // Add the upsell products to the properties
      ...Object.fromEntries(upsellProductsFormData),
    };
    Object.assign(cleanFormJsonData, { properties: formDataProperties });
    return { cleanFormJsonData, upsellProducts };
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
